import { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Calendar } from 'lucide-react';
import { track } from '../lib/mixpanel';

const CALENDLY_URL =
  (import.meta.env.VITE_CALENDLY_URL as string | undefined) ??
  'https://calendly.com/sagentivum';

const CALENDLY_SCRIPT_URL = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_STYLE_URL  = 'https://assets.calendly.com/assets/external/widget.css';

export default function FloatingBookCTA({ show }: { show: boolean }) {
  // Pre-load Calendly as soon as this component mounts (i.e. when the
  // diagnosis is complete) so it's ready by the time the user clicks.
  useEffect(() => {
    if (!show) return;
    if (!document.querySelector(`link[href="${CALENDLY_STYLE_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = CALENDLY_STYLE_URL;
      document.head.appendChild(link);
    }
    if (!document.querySelector(`script[src="${CALENDLY_SCRIPT_URL}"]`)) {
      const script = document.createElement('script');
      script.src = CALENDLY_SCRIPT_URL;
      script.async = true;
      document.body.appendChild(script);
    }
  }, [show]);

  const handleClick = () => {
    track('floating_book_cta_clicked'); // event 11
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: `${CALENDLY_URL}?hide_event_type_details=0&hide_gdpr_banner=1`,
      });
    } else {
      window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-6 right-6 z-50"
        >
          <button
            type="button"
            onClick={handleClick}
            className="flex items-center gap-2.5 bg-green-700 text-paper pl-5 pr-4 py-3 rounded-full shadow-[0_4px_20px_rgba(42,71,42,0.35)] hover:bg-green-800 transition-colors text-[14px] font-semibold"
          >
            <Calendar size={15} />
            Book the call
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
